import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import { useVendors } from '../useVendors';
import { usePurchaseOrders } from '../usePurchaseOrders';
import { usePurchaseOrder } from '../usePurchaseOrder';
import { useCreatePurchaseOrder } from '../useCreatePurchaseOrder';
import { useUpdatePOStatus } from '../useUpdatePOStatus';
import { useCreateGoodsReceipt } from '../useCreateGoodsReceipt';
import { searchMedicines } from '../api';
import GrnLineEditor, { toGrnItemInput, type DraftGrnLine } from './GrnLineEditor';
import ProcurementInvoiceQRModal from './ProcurementInvoiceQRModal';
import { linesFromPoItems } from './poReceiveLines';
import { linesFromExtractedInvoice } from './extractedInvoiceLines';
import { useInvoiceUploadFlow } from './useInvoiceUploadFlow';
import type { POStatus, MedicineSearchResult, PurchaseOrder } from '../types';

// medicine_id is OPTIONAL here (unlike Goods Receipt, §21.1, where it's
// required) — a PO can legitimately name a drug not yet in the catalog at
// all, since it hasn't been received yet. medicine_name stays the primary,
// always-required field (free-text fallback/snapshot, same convention as
// prescription_items.drug_name); picking a search result just additionally
// resolves medicine_id. See docs/HMS_v4_Drug_Master_Formulary_Architecture.md §21.2.
type DraftPOLine = { medicine_id: number | null; medicine_name: string; quantity_ordered: string; expected_unit_cost: string };
const emptyLine = (): DraftPOLine => ({ medicine_id: null, medicine_name: '', quantity_ordered: '', expected_unit_cost: '' });

// Free-text medicine_name with an optional search-and-resolve underneath —
// picking a suggestion additionally sets medicine_id; typing without
// picking one leaves it unresolved (a legitimate PO for a drug not yet in
// the catalog). §21.2.
function POMedicineField({ value, medicineId, onChange, onPick }: {
  value: string; medicineId: number | null; onChange: (v: string) => void; onPick: (r: MedicineSearchResult) => void;
}) {
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  useEffect(() => {
    if (medicineId || value.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => { searchMedicines(value).then(setResults).catch(() => setResults([])); }, 250);
    return () => clearTimeout(t);
  }, [value, medicineId]);

  return (
    <div className="relative">
      <Input
        placeholder="Medicine name"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-xs ${medicineId ? 'border-teal' : ''}`}
        title={medicineId ? 'Resolved to an existing catalog product' : undefined}
      />
      {results.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-[var(--radius)] border border-line bg-surface shadow-md">
          {results.map((r) => (
            <button key={r.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-canvas" onClick={() => { onPick(r); setResults([]); }}>
              {r.generic_name}{r.brand_name ? ` — ${r.brand_name}` : ''}{r.strength ? ` ${r.strength}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_VARIANT: Record<POStatus, 'success' | 'danger' | 'info' | 'neutral'> = {
  received: 'success',
  cancelled: 'danger',
  sent: 'info',
  draft: 'neutral',
};

/** The "Mark Received" panel for one PO — pre-fills a GrnLineEditor draft
 * from the PO's ordered lines and submits through the same
 * POST /goods-receipts every other GRN uses, with po_id set (which flips
 * the PO to 'received' server-side; single receive-and-close, no partial-
 * receipt lifecycle). */
function ReceivePOPanel({ po, onDone, onCancel }: { po: PurchaseOrder; onDone: () => void; onCancel: () => void }) {
  const { gstPercentageOptions } = useBillingSettings();
  const detailQuery = usePurchaseOrder(po.id);
  const createGrn = useCreateGoodsReceipt();
  const [invoiceNo, setInvoiceNo] = useState('');
  const [items, setItems] = useState<DraftGrnLine[] | null>(null);
  const [validationError, setValidationError] = useState('');
  const [showQr, setShowQr] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // An uploaded invoice replaces the PO-derived draft outright — the
  // invoice is the ground truth for what actually arrived and what to pay,
  // same behavior as GoodsReceiptPanel's standalone upload.
  const invoiceFlow = useInvoiceUploadFlow((lines) => setItems(linesFromExtractedInvoice(lines, gstPercentageOptions)));

  useEffect(() => {
    if (detailQuery.data && items === null) setItems(linesFromPoItems(detailQuery.data.items, gstPercentageOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.data]);

  if (detailQuery.isLoading || !items) return <div className="mb-3.5 rounded-[var(--radius)] bg-canvas p-3"><LoadingState /></div>;

  const handleFilePick = (file: File | undefined) => {
    invoiceFlow.handleFilePick(file, po.id);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = () => {
    const clean = items.filter((i) => i.medicine_id && i.batch_number.trim() && i.expiry_date && i.quantity_received && i.mrp);
    if (!clean.length) {
      setValidationError('At least one complete item (medicine resolved, batch, expiry, qty, MRP) is required');
      return;
    }
    setValidationError('');
    createGrn.mutate(
      {
        po_id: po.id,
        vendor_id: po.vendor_id,
        vendor_invoice_number: invoiceNo.trim() || null,
        items: clean.map(toGrnItemInput),
      },
      { onSuccess: onDone }
    );
  };

  const error = validationError || (createGrn.error instanceof ApiError ? createGrn.error.message : null);

  return (
    <div className="mb-3.5 rounded-[var(--radius)] border border-line bg-canvas p-3">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-1.5">
        <div className="text-[13px] font-semibold">Receiving {po.po_number} — {po.vendor_name}</div>
        <div className="flex flex-wrap gap-1.5">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/tiff,application/pdf" className="hidden" onChange={(e) => handleFilePick(e.target.files?.[0])} />
          <Button variant="ghost" size="sm" disabled={invoiceFlow.isBusy} loading={invoiceFlow.isBusy} onClick={() => fileRef.current?.click()}>
            📄 Upload vendor invoice
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowQr(true)}>📱 Scan invoice via phone</Button>
        </div>
      </div>
      <div className="mb-2.5 text-[11.5px] text-ink-muted">
        Quantities and cost default to what was ordered — edit any line to match what actually arrived, then fill in batch/expiry/MRP for each.
        Or upload the vendor's invoice above to fill everything in automatically.
      </div>
      {invoiceFlow.uploadNotice && (
        <div className="mb-2.5 flex items-center gap-2 rounded-[var(--radius)] bg-info-soft px-3 py-2 text-[11.5px] text-info-ink">
          {invoiceFlow.isBusy && (
            <span aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
          {invoiceFlow.uploadNotice}
        </div>
      )}
      {error && <div className="mb-2.5 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      <FormField label="Vendor invoice no." className="mb-2.5"><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} /></FormField>
      <GrnLineEditor items={items} onChange={setItems} gstPercentageOptions={gstPercentageOptions} />
      <div className="mt-2.5 flex gap-2">
        <Button size="sm" disabled={createGrn.isPending} loading={createGrn.isPending} onClick={submit}>
          {createGrn.isPending ? 'Saving…' : 'Save receipt & add to stock'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
      {showQr && (
        <ProcurementInvoiceQRModal
          poId={po.id}
          onClose={() => setShowQr(false)}
          onReceived={(lines) => { invoiceFlow.handleReceived(lines); setShowQr(false); }}
        />
      )}
    </div>
  );
}

export default function PurchaseOrdersPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [creating, setCreating] = useState(false);
  const [vendorId, setVendorId] = useState('');
  // Line item quantity/cost are kept as raw strings while editing (matching
  // every other numeric-line-item form this session) and parsed on submit.
  const [items, setItems] = useState<DraftPOLine[]>([emptyLine()]);
  const [validationError, setValidationError] = useState('');
  const [receivingPoId, setReceivingPoId] = useState<number | null>(null);

  const vendorsQuery = useVendors();
  const posQuery = usePurchaseOrders();
  const createPO = useCreatePurchaseOrder();
  const updateStatus = useUpdatePOStatus();

  const updateItem = (idx: number, patch: Partial<(typeof items)[number]>) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const submit = () => {
    const clean = items.filter((i) => i.medicine_name.trim() && i.quantity_ordered);
    if (!vendorId || !clean.length) { setValidationError('Vendor and at least one item are required'); return; }
    setValidationError('');
    createPO.mutate(
      {
        vendor_id: Number(vendorId),
        items: clean.map((i) => ({
          medicine_id: i.medicine_id ?? undefined,
          medicine_name: i.medicine_name,
          quantity_ordered: parseInt(i.quantity_ordered, 10),
          expected_unit_cost: i.expected_unit_cost ? parseFloat(i.expected_unit_cost) : null,
        })),
      },
      {
        onSuccess: () => {
          setVendorId('');
          setItems([emptyLine()]);
          setCreating(false);
        },
      }
    );
  };

  const error = validationError || (createPO.error instanceof ApiError ? createPO.error.message : null);
  const vendors = vendorsQuery.data;
  const pos = posQuery.data;

  if (vendorsQuery.isLoading || posQuery.isLoading || vendors === undefined || pos === undefined) return <LoadingState />;

  const receivingPo = receivingPoId != null ? pos.find((p) => p.id === receivingPoId) : null;

  return (
    <Card>
      <div className="mb-3.5 flex items-center justify-between">
        <div className="text-sm font-semibold">Purchase Orders</div>
        {isAdmin && !creating && (
          <Button variant="ghost" size="sm" disabled={!vendors.length} onClick={() => setCreating(true)}>+ New PO</Button>
        )}
      </div>
      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      {creating && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-canvas p-3">
          <FormField label="Vendor" className="mb-2.5">
            <NativeSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Select vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </NativeSelect>
          </FormField>
          {items.map((it, idx) => (
            <div key={idx} className="mb-1.5 grid grid-cols-[2fr_1fr_1fr] gap-1.5">
              <POMedicineField
                value={it.medicine_name}
                medicineId={it.medicine_id}
                onChange={(medicine_name) => updateItem(idx, { medicine_name, medicine_id: null })}
                onPick={(r) => updateItem(idx, { medicine_id: r.id, medicine_name: `${r.generic_name}${r.brand_name ? ` — ${r.brand_name}` : ''}` })}
              />
              <Input type="number" placeholder="Qty" value={it.quantity_ordered} onChange={(e) => updateItem(idx, { quantity_ordered: e.target.value })} className="text-xs" />
              <Input type="number" placeholder="Est. unit cost" value={it.expected_unit_cost} onChange={(e) => updateItem(idx, { expected_unit_cost: e.target.value })} className="text-xs" />
            </div>
          ))}
          <Button variant="ghost" size="sm" className="mb-2.5" onClick={() => setItems((p) => [...p, emptyLine()])}>
            + Add line
          </Button>
          <div className="flex gap-2">
            <Button size="sm" disabled={createPO.isPending} loading={createPO.isPending} onClick={submit}>Create PO</Button>
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {receivingPo && (
        <ReceivePOPanel po={receivingPo} onDone={() => setReceivingPoId(null)} onCancel={() => setReceivingPoId(null)} />
      )}
      <div className="flex flex-col gap-2">
        {pos.map((po) => (
          <div key={po.id} className="rounded-[var(--radius)] border border-line p-2.5">
            <div className="flex justify-between">
              <div className="text-[13px] font-semibold">{po.po_number} — {po.vendor_name}</div>
              <Badge variant={STATUS_VARIANT[po.status]}>{po.status}</Badge>
            </div>
            <div className="mt-1 text-[11.5px] text-ink-muted">{po.items.map((i) => `${i.medicine_name} ×${i.quantity_ordered}`).join(', ')}</div>
            {isAdmin && po.status === 'draft' && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1.5"
                disabled={updateStatus.isPending}
                onClick={() => updateStatus.mutate({ id: po.id, status: 'sent' })}
              >
                Mark as sent to vendor
              </Button>
            )}
            {po.status === 'sent' && receivingPoId !== po.id && (
              <Button variant="ghost" size="sm" className="mt-1.5" onClick={() => setReceivingPoId(po.id)}>
                📥 Mark Received
              </Button>
            )}
          </div>
        ))}
        {pos.length === 0 && <EmptyState label="No purchase orders yet." />}
      </div>
    </Card>
  );
}
